<script setup>
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/vue'
import { useField } from 'vee-validate'
import { ref, computed, watch, nextTick } from 'vue'

import { usePopover, useClickOutside } from '@/composables/usePopover'

import ImoContextMenuDivider from './ImoContextMenuDivider.vue'
import ImoContextMenuItem from './ImoContextMenuItem.vue'

/**
 * Props do ImoContextMenu.
 * @param {string} name - Nome do campo — usado como `id` do trigger e como chave de validação (`vee-validate`).
 * @param {string} [label] - Label exibida acima do trigger. Sem label quando omitida.
 * @param {string} [placeholder='Selecione'] - Texto do trigger quando nenhuma opção está selecionada.
 * @param {Array<{value: *, label: string, group?: string}>} [options=[]] - Itens com o mesmo `group`
 *   (em sequência) são exibidos sob o mesmo título e separados por um divisor do próximo grupo.
 *   Opcional quando o conteúdo do menu é composto manualmente via slot `content`
 *   (ex: menu de ações com submenus, em vez de multiselect).
 * @param {boolean} [disabled=false] - Desabilita o trigger e impede a abertura do menu.
 * @param {boolean} [multiple=true] - Quando `true` (padrão), funciona como multiselect: `model`
 *   é um Array, cada clique alterna a opção e o menu permanece aberto. Quando `false`, funciona
 *   como seleção única (substituto de `SelectInput`): `model` é um valor escalar, o clique
 *   substitui a seleção e fecha o menu, e a lista de tags não é exibida.
 * @param {boolean} [withTags=true] - Exibe os itens selecionados como tags removíveis abaixo do
 *   trigger. Desative para um multiselect "compacto" (só o contador no trigger) — grupos
 *   continuam funcionando independente disso, via `options[].group`. Sem efeito quando
 *   `multiple` é `false`.
 * @param {boolean} [withDividers=true] - No DS, divisor e título de grupo são recursos
 *   independentes (ex: agrupamento só com título, sem divisor). Desative quando os grupos não
 *   devem ser separados por linha.
 */
const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  placeholder: {
    type: String,
    default: 'Selecione',
  },
  options: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  withTags: {
    type: Boolean,
    default: true,
  },
  withDividers: {
    type: Boolean,
    default: true,
  },
})

// Cores variam por opção (não só laranja) para diferenciar visualmente os itens
// selecionados quando exibidos como tags.
const TAG_VARIANTS = ['primary', 'secondary', 'info', 'success', 'warning']

// `type: null` desativa a validação de tipo — `model` é Array em modo multiselect
// (padrão) ou um valor escalar em modo `multiple: false` (seleção única). Sem
// `default`: o watch abaixo normaliza pra `[]` no modo multiselect; no modo de
// seleção única, `undefined` já é tratado como "nada selecionado".
const model = defineModel({ type: null })

const { value, errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
})

// ImoFilter só popula a query (e portanto o modelValue) no seu próprio onMounted,
// que roda depois do onMounted deste componente — por isso a normalização precisa
// reagir a qualquer mudança de model, não só à montagem inicial. Só se aplica ao
// modo multiselect: em seleção única o model já é o valor escalar puro.
watch(
  model,
  v => {
    if (!props.multiple || Array.isArray(v)) return
    model.value = typeof v === 'string' ? v.split(',').filter(Boolean) : v ? [v] : []
  },
  { immediate: true },
)

const hasSelection = computed(() =>
  props.multiple ? model.value.length > 0 : model.value !== '' && model.value != null,
)

const isSelected = optionValue =>
  props.multiple ? model.value.includes(optionValue) : model.value === optionValue

const buttonRef = ref(null)
const contentRef = ref(null)
const { isOpen, toggle, close } = usePopover()

const { x, y, strategy } = useFloating(buttonRef, contentRef, {
  placement: 'bottom-start',
  middleware: [offset(4), flip(), shift()],
  whileElementsMounted: autoUpdate,
})

useClickOutside([buttonRef, contentRef], close)

// Foca o primeiro item ao abrir, seguindo o padrão ARIA de menu — o usuário que abre
// via teclado já cai navegável, sem precisar de um Tab/Arrow extra.
watch(isOpen, async open => {
  if (!open) return
  await nextTick()
  focusMenuItem(0)
})

// Mantém o item focado visível dentro do container rolável (padrão adotado em
// ContactInput/ClientInput), sem precisar recalcular scrollTop manualmente.
const focusMenuItem = index => {
  const items = contentRef.value?.querySelectorAll('button[role="menuitem"]:not(:disabled)')
  if (!items?.length) return
  const wrapped = ((index % items.length) + items.length) % items.length
  items[wrapped].focus()
  items[wrapped].scrollIntoView({ block: 'nearest' })
}

// Navegação por seta/Home/End entre os itens visíveis do menu (padrão + slot `content`
// customizado), delegada no container em vez de em cada item individualmente.
const handleMenuKeydown = event => {
  const items = contentRef.value?.querySelectorAll('button[role="menuitem"]:not(:disabled)')
  if (!items?.length) return

  const currentIndex = Array.prototype.indexOf.call(items, document.activeElement)

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusMenuItem(currentIndex + 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      focusMenuItem(currentIndex - 1)
      break
    case 'Home':
      event.preventDefault()
      focusMenuItem(0)
      break
    case 'End':
      event.preventDefault()
      focusMenuItem(items.length - 1)
      break
  }
}

const groupedOptions = computed(() => {
  const groups = []
  let current = null

  for (const option of props.options) {
    const groupKey = option.group ?? null
    if (!current || current.group !== groupKey) {
      current = { group: groupKey, options: [] }
      groups.push(current)
    }
    current.options.push(option)
  }

  return groups
})

const optionLabel = optionValue =>
  props.options.find(option => option.value === optionValue)?.label ?? optionValue

// Hash estável do value (não do índice em options) — reordenar ou inserir opções
// não deve trocar a cor de filtros já salvos.
const tagVariant = optionValue => {
  const hash = String(optionValue)
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return TAG_VARIANTS[hash % TAG_VARIANTS.length]
}

const triggerText = computed(() => {
  if (!hasSelection.value) return props.placeholder
  if (!props.multiple) return optionLabel(model.value)
  return `${model.value.length} selecionado${model.value.length > 1 ? 's' : ''}`
})

const toggleOption = optionValue => {
  if (!props.multiple) {
    value.value = optionValue
    close()
    return
  }

  const current = Array.isArray(value.value) ? [...value.value] : []

  if (current.includes(optionValue)) {
    value.value = current.filter(v => v !== optionValue)
  } else {
    value.value = [...current, optionValue]
  }
}

const triggerWidth = ref(0)

const handleToggle = () => {
  if (props.disabled) return
  triggerWidth.value = buttonRef.value?.offsetWidth ?? 0
  toggle()
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <label v-if="label" :for="name" class="text-neutral-60 text-sm">{{ label }}</label>

    <div class="relative">
      <div ref="buttonRef">
        <slot name="trigger" :toggle="handleToggle" :isOpen="isOpen" :disabled="disabled">
          <button
            :id="name"
            type="button"
            :disabled="disabled"
            aria-haspopup="menu"
            :aria-expanded="isOpen"
            @click="handleToggle"
            @keydown.escape="close"
            class="context-menu-trigger"
            :class="{ error: errorMessage }"
          >
            <span class="truncate" :class="hasSelection ? 'text-neutral-90' : 'text-neutral-40'">
              {{ triggerText }}
            </span>
            <ImoIcon
              name="arrow-down-02"
              :size="18"
              class="text-neutral-40 pointer-events-none absolute top-1/2 right-3 shrink-0 -translate-y-1/2 transition-transform duration-200"
              :class="{ 'rotate-180': isOpen }"
            />
          </button>
        </slot>
      </div>

      <Teleport to="body">
        <Transition
          enter-active-class="transition-opacity duration-150 ease-out"
          leave-active-class="transition-opacity duration-100 ease-in"
          enter-from-class="opacity-0"
          leave-to-class="opacity-0"
        >
          <div
            v-if="isOpen"
            ref="contentRef"
            role="menu"
            aria-orientation="vertical"
            tabindex="-1"
            @keydown.escape="close"
            @keydown="handleMenuKeydown"
            :style="{
              position: strategy,
              top: `${y ?? 0}px`,
              left: `${x ?? 0}px`,
              minWidth: `${triggerWidth}px`,
            }"
            class="border-neutral-20 z-50 flex w-fit flex-col gap-0.5 rounded-lg border bg-white px-1.5 py-1 shadow-lg"
          >
            <slot name="content" :close="close">
              <div
                class="scrollbar-thin scrollbar-thumb-neutral-20 scrollbar-track-transparent flex max-h-72 flex-col gap-0.5 overflow-y-auto"
              >
                <template v-for="(group, groupIndex) in groupedOptions" :key="groupIndex">
                  <ImoContextMenuDivider v-if="groupIndex > 0 && withDividers" />
                  <ImoContextMenuItem
                    v-if="group.group"
                    variant="group-title"
                    :text="group.group"
                  />
                  <ImoContextMenuItem
                    v-for="option in group.options"
                    :key="option.value"
                    variant="default"
                    :text="option.label"
                    :icon="option.icon"
                    :selected="isSelected(option.value)"
                    @click="toggleOption(option.value)"
                  />
                </template>
              </div>
            </slot>
          </div>
        </Transition>
      </Teleport>
    </div>

    <div v-if="multiple && withTags && model.length" class="flex flex-wrap gap-2">
      <ImoBadge
        v-for="optionValue in model"
        :key="optionValue"
        :variant="tagVariant(optionValue)"
        size="sm"
      >
        <span class="flex items-center gap-1">
          {{ optionLabel(optionValue) }}
          <button
            type="button"
            @click="toggleOption(optionValue)"
            :aria-label="`Remover ${optionLabel(optionValue)}`"
            class="flex items-center"
          >
            <ImoIcon name="close" :size="12" />
          </button>
        </span>
      </ImoBadge>
    </div>

    <span class="text-error-60 text-[10px]">{{ errorMessage }}</span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.context-menu-trigger {
  @apply border-neutral-30 flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-4 py-2 text-left text-sm;
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40 disabled:pointer-events-none;
}

.context-menu-trigger:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
